import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import StockThumb from "@/components/StockThumb";
import StockFormDialog from "@/components/StockFormDialog";
import BulkThresholdsDialog from "@/components/BulkThresholdsDialog";
import QuickViewInventory from "@/components/QuickViewInventory";
import ItemHistoryDialog from "@/components/ItemHistoryDialog";
import Lightbox from "@/components/Lightbox";
import DepartmentChips, { DepartmentBadges } from "@/components/DepartmentChips";
import MaterialChips from "@/components/MaterialChips";
import CategoryChips from "@/components/CategoryChips";
import SupplierChips from "@/components/SupplierChips";
import { ColourBadge } from "@/components/ColourChips";
import { useColours } from "@/contexts/ColoursContext";
import ManageStockTypesDialog from "@/components/ManageStockTypesDialog";
import ManageDepartmentsDialog from "@/components/ManageDepartmentsDialog";
import ManageMaterialsDialog from "@/components/ManageMaterialsDialog";
import ManageColoursDialog from "@/components/ManageColoursDialog";
import ManageStockCategoriesDialog from "@/components/ManageStockCategoriesDialog";
import ManageRequestersDialog from "@/components/ManageRequestersDialog";
import ExportButtons from "@/components/ExportButtons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, Edit3, Search, History as HistoryIcon, Tags, Building2, Layers, Settings2, ChevronDown, Users, Cog, Printer, LayoutGrid, Table as TableIcon, ShoppingCart } from "lucide-react";
import { packSummary, fmtQty } from "@/lib/pack";
import AddToDraftDialog from "@/components/AddToDraftDialog";
import DiscontinuedBadge from "@/components/DiscontinuedBadge";

const Catalog = () => {
  const { user } = useAuth();
  const { swatchOf: colourSwatchOf, swatchImageOf: colourImageOf } = useColours();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [dept, setDept] = useState("all");
  const [material, setMaterial] = useState("all");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [bulkThresholdsOpen, setBulkThresholdsOpen] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [lightboxFor, setLightboxFor] = useState(null);
  const [manageTypesOpen, setManageTypesOpen] = useState(false);
  const [manageDeptsOpen, setManageDeptsOpen] = useState(false);
  const [manageMatsOpen, setManageMatsOpen] = useState(false);
  const [manageColoursOpen, setManageColoursOpen] = useState(false);
  const [manageCatsOpen, setManageCatsOpen] = useState(false);
  const [manageRequestersOpen, setManageRequestersOpen] = useState(false);
  const [addToDraftFor, setAddToDraftFor] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [category, setCategory] = useState("all");
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [highlightId, setHighlightId] = useState(null);
  // Persist the Quick-View toggle across visits — managers post-import
  // usually want to keep the table open until every item is fully tagged.
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("catalog-view-mode") || "cards",
  );
  const switchView = (mode) => {
    setViewMode(mode);
    localStorage.setItem("catalog-view-mode", mode);
  };
  // Lookup of team-member id → name so each inventory card can show the
  // small "Orders: <name>" chip without needing a JOIN on the server.
  const [usersById, setUsersById] = useState({});
  // Flat list variant for the Quick View <select> — declared AFTER the
  // useState above so the reference is initialised. Order matters in JS.
  const usersList = useMemo(() => Object.values(usersById), [usersById]);

  const load = async () => {
    try {
      const { data } = await api.get("/stock");
      setItems(data);
    } catch {
      toast.error("Could not load catalog");
    }
  };

  const loadUsers = async () => {
    try {
      const { data } = await api.get("/users/public");
      const map = {};
      (data || []).forEach((u) => {
        map[u.id] = u;
      });
      setUsersById(map);
    } catch {
      // Non-fatal — the "Orders: <name>" chip simply falls back to hidden.
    }
  };

  // Called after a save/clone — reloads the list and scrolls the newly-saved
  // (or cloned) card into view with a brief yellow highlight so the user has
  // a visible confirmation that the refresh happened.
  const handleSaved = async (saved) => {
    await load();
    if (!saved?.id) return;
    setHighlightId(saved.id);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="catalog-item-${saved.id}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
    setTimeout(() => setHighlightId(null), 2400);
  };

  useEffect(() => {
    load();
    loadUsers();
    api.get("/suppliers").then(({ data }) => setSuppliers(data || [])).catch(() => {});
  }, []);

  // Staff are hard-filtered server-side; default chip stays "all".

  // Materials actually used by items in the currently-selected department —
  // intentionally empty when "All depts" so the chip row stays hidden until
  // the user narrows the scope.
  const relevantMaterials = useMemo(() => {
    if (dept === "all") return [];
    const inScope = items.filter((it) => {
      const list = it.departments?.length ? it.departments : [it.department].filter(Boolean);
      return list.includes(dept);
    });
    return Array.from(new Set(inScope.map((it) => it.material).filter(Boolean)));
  }, [items, dept]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (dept !== "all") {
        const list = it.departments?.length ? it.departments : [it.department].filter(Boolean);
        if (!list.includes(dept)) return false;
      }
      if (material !== "all" && (it.material || "") !== material) return false;
      if (category !== "all" && (it.category || "") !== category) return false;
      if (
        supplierFilter !== "all" &&
        (it.supplier_default || "") !== supplierFilter
      )
        return false;
      if (!q) return true;
      return (
        it.name.toLowerCase().includes(q) ||
        (it.sku || "").toLowerCase().includes(q) ||
        (it.supplier_default || "").toLowerCase().includes(q) ||
        (it.material || "").toLowerCase().includes(q) ||
        (it.colour || "").toLowerCase().includes(q)
      );
    });
  }, [items, search, dept, material, category, supplierFilter]);

  const relevantCategories = useMemo(() => {
    const inScope = items.filter((it) => {
      if (dept !== "all") {
        const list = it.departments?.length ? it.departments : [it.department].filter(Boolean);
        if (!list.includes(dept)) return false;
      }
      if (material !== "all" && (it.material || "") !== material) return false;
      return true;
    });
    return Array.from(new Set(inScope.map((it) => it.category).filter(Boolean)));
  }, [items, dept, material]);

  // Suppliers present after dept / material / category filters. Feeds
  // SupplierChips popover so warehouse staff can narrow the catalog to a
  // single supplier when browsing inventory.
  const relevantSuppliers = useMemo(() => {
    const inScope = items.filter((it) => {
      if (dept !== "all") {
        const list = it.departments?.length ? it.departments : [it.department].filter(Boolean);
        if (!list.includes(dept)) return false;
      }
      if (material !== "all" && (it.material || "") !== material) return false;
      if (category !== "all" && (it.category || "") !== category) return false;
      return true;
    });
    return Array.from(
      new Set(inScope.map((it) => it.supplier_default).filter(Boolean)),
    );
  }, [items, dept, material, category]);

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="catalog-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="label-eyebrow">Materials & consumables</div>
          <h1 className="font-display text-5xl leading-none mt-2">Inventory</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ExportButtons
            entity="stock"
            rows={items}
            csvFilename="inventory"
            testidPrefix="catalog-export"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="catalog-manage-lists-btn"
                className="nb-btn bg-white px-4 py-4 flex items-center gap-2"
              >
                <Settings2 size={18} strokeWidth={2.5} />
                Manage lists
                <ChevronDown size={14} strokeWidth={2.5} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              data-testid="catalog-manage-lists-menu"
              className="border-2 border-black rounded-xl shadow-[4px_4px_0_#001740] bg-white min-w-[14rem] p-1.5"
            >
              <DropdownMenuItem
                data-testid="catalog-manage-types-btn"
                onClick={() => setManageTypesOpen(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-semibold cursor-pointer rounded-md focus:bg-[#F1E7FA] focus:text-[#5B3A99]"
              >
                <Tags size={16} strokeWidth={2.5} /> Stock types
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="catalog-manage-materials-btn"
                onClick={() => setManageMatsOpen(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-semibold cursor-pointer rounded-md focus:bg-[#F1E7FA] focus:text-[#5B3A99]"
              >
                <Layers size={16} strokeWidth={2.5} /> Materials
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="catalog-manage-categories-btn"
                onClick={() => setManageCatsOpen(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-semibold cursor-pointer rounded-md focus:bg-[#F1E7FA] focus:text-[#5B3A99]"
              >
                <Tags size={16} strokeWidth={2.5} /> Categories
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="catalog-manage-colours-btn"
                onClick={() => setManageColoursOpen(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-semibold cursor-pointer rounded-md focus:bg-[#F1E7FA] focus:text-[#5B3A99]"
              >
                <Layers size={16} strokeWidth={2.5} /> Colours
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="catalog-manage-depts-btn"
                onClick={() => setManageDeptsOpen(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-semibold cursor-pointer rounded-md focus:bg-[#F1E7FA] focus:text-[#5B3A99]"
              >
                <Building2 size={16} strokeWidth={2.5} /> Departments
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="catalog-manage-requesters-btn"
                onClick={() => setManageRequestersOpen(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-semibold cursor-pointer rounded-md focus:bg-[#F1E7FA] focus:text-[#5B3A99]"
              >
                <Users size={16} strokeWidth={2.5} /> Requesters
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <button
            data-testid="catalog-print-stocktake-btn"
            onClick={() => {
              // Encode active filters onto the URL so the print page reflects
              // exactly what the manager is currently seeing.
              const p = new URLSearchParams();
              if (dept !== "all") p.set("dept", dept);
              if (material !== "all") p.set("material", material);
              if (category !== "all") p.set("category", category);
              if (supplierFilter !== "all") p.set("supplier", supplierFilter);
              if (search.trim()) p.set("q", search.trim());
              const qs = p.toString();
              window.open(
                `/stocktake-print${qs ? `?${qs}` : ""}`,
                "_blank",
                "noopener",
              );
            }}
            className="nb-btn bg-white px-4 py-4 flex items-center gap-2"
            title="Open a print-friendly stocktake sheet in a new tab"
          >
            <Printer size={18} strokeWidth={2.5} />
            Print stocktake
          </button>
          <button
            data-testid="catalog-bulk-threshold-btn"
            onClick={() => setBulkThresholdsOpen(true)}
            className="nb-btn bg-white px-4 py-4 flex items-center gap-2"
            title="Set low-stock thresholds on many items at once"
          >
            <Layers size={18} strokeWidth={2.5} />
            Bulk thresholds
          </button>
          <div
            className="nb-card p-1 flex items-center gap-1"
            data-testid="catalog-view-toggle"
            role="group"
            aria-label="View mode"
          >
            <button
              data-testid="catalog-view-cards"
              onClick={() => switchView("cards")}
              className={`px-3 py-2.5 text-xs font-bold flex items-center gap-1 rounded-md transition-colors ${
                viewMode === "cards"
                  ? "bg-[#001740] text-white"
                  : "text-[#001740] hover:bg-[#F4F8FB]"
              }`}
              title="Show inventory as image cards (default)"
            >
              <LayoutGrid size={14} strokeWidth={2.5} /> Cards
            </button>
            <button
              data-testid="catalog-view-table"
              onClick={() => switchView("table")}
              className={`px-3 py-2.5 text-xs font-bold flex items-center gap-1 rounded-md transition-colors ${
                viewMode === "table"
                  ? "bg-[#001740] text-white"
                  : "text-[#001740] hover:bg-[#F4F8FB]"
              }`}
              title="Quick View — table with editable fields for bulk fill-in"
            >
              <TableIcon size={14} strokeWidth={2.5} /> Quick View
            </button>
          </div>
          <button
            data-testid="catalog-add-btn"
            onClick={() => {
              setEditing(null);
              setOpen(true);
            }}
            className="nb-btn nb-btn-primary px-5 py-4 flex items-center gap-2"
          >
            <Plus size={20} strokeWidth={2.5} /> Add item
          </button>
        </div>
      </div>

      <div className="relative mb-4">
        <Search
          size={20}
          strokeWidth={2.5}
          className="absolute left-3.5 top-1/2 -translate-y-1/2"
        />
        <input
          data-testid="catalog-search-input"
          className="nb-input pl-12 h-14 text-lg"
          placeholder="Search catalog…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="mb-5">
        <DepartmentChips value={dept} onChange={setDept} testidPrefix="catalog-dept" />
      </div>

      <div className="mb-3">
        <MaterialChips
          value={material}
          onChange={setMaterial}
          testidPrefix="catalog-mat"
          relevantNames={relevantMaterials}
        />
      </div>
      <div className="mb-3">
        <CategoryChips
          value={category}
          onChange={setCategory}
          testidPrefix="catalog-cat"
          relevantNames={relevantCategories}
        />
      </div>
      <div className="mb-3">
        <SupplierChips
          value={supplierFilter}
          onChange={setSupplierFilter}
          testidPrefix="catalog-supplier"
          relevantNames={relevantSuppliers}
        />
      </div>

      {viewMode === "table" ? (
        <QuickViewInventory
          items={filtered}
          users={usersList}
          onSaved={load}
        />
      ) : (
      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        data-testid="catalog-grid"
      >
        {filtered.map((it) => {
          const photos = it.image_paths?.length
            ? it.image_paths
            : it.image_path
            ? [it.image_path]
            : [];
          return (
            <div
              key={it.id}
              className={`nb-card p-4 flex gap-4 transition-all ${
                highlightId === it.id ? "ring-4 ring-[#FFB300] shadow-lg" : ""
              }`}
              data-testid={`catalog-item-${it.id}`}
            >
              <StockThumb
                item={it}
                size="h-24 w-24"
                onClick={photos.length ? () => setLightboxFor(photos) : undefined}
              />
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg leading-tight truncate">{it.name}</div>
                {it.discontinued && (
                  <div className="mt-1">
                    <DiscontinuedBadge
                      item={it}
                      onUseReplacement={(rid) => {
                        const r = items.find((x) => x.id === rid);
                        if (r) setEditing(r);
                      }}
                    />
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5 mt-1 items-center">
                  <span className="label-eyebrow px-1.5 py-0.5 rounded border-2 border-black bg-white">
                    {it.type}
                  </span>
                  {it.size_spec && (
                    <span className="label-eyebrow px-1.5 py-0.5 rounded border-2 border-black bg-white">
                      {it.size_spec}
                    </span>
                  )}
                  {it.pack_size && it.pack_unit && (
                    <span className="label-eyebrow px-1.5 py-0.5 rounded border-2 border-black bg-[#FFEC8A]">
                      {it.pack_size} {it.pack_unit}/{it.unit}
                    </span>
                  )}
                  {it.material && (
                    <span
                      className="label-eyebrow px-1.5 py-0.5 rounded border-2 border-black bg-[#F1E7FA] text-[#5B3A99]"
                      data-testid={`material-badge-${it.id}`}
                    >
                      {it.material}
                    </span>
                  )}
                  {it.colour && (
                    <ColourBadge
                      name={it.colour}
                      swatch={colourSwatchOf(it.colour)}
                      swatchImage={colourImageOf(it.colour)}
                    />
                  )}
                  {photos.length > 1 && (
                    <span className="label-eyebrow px-1.5 py-0.5 rounded border-2 border-black bg-[#228EBF]">
                      {photos.length} pics
                    </span>
                  )}
                  {it.is_hazardous && (
                    <span className="label-eyebrow px-1.5 py-0.5 rounded-full bg-[#E5263F]/15 text-[#E5263F] border border-[#E5263F]/30">
                      ⚠ hazardous
                    </span>
                  )}
                  {it.linked_machines_count > 0 && (
                    <span
                      data-testid={`linked-machines-badge-${it.id}`}
                      className="label-eyebrow px-1.5 py-0.5 rounded-full bg-[#F1E7FA] text-[#5B3A99] border-2 border-[#5B3A99] inline-flex items-center gap-1"
                      title={`Spare for ${it.linked_machines_count} machine${it.linked_machines_count === 1 ? "" : "s"}`}
                    >
                      <Cog size={10} strokeWidth={2.5} />
                      {it.linked_machines_count}
                    </span>
                  )}
                  {it.thickness_mm != null && (
                    <span
                      data-testid={`thickness-badge-${it.id}`}
                      className="label-eyebrow font-mono-num px-1.5 py-0.5 rounded-full bg-[#DFEEF7] text-[#0F3D5C] border border-[#0F3D5C]/30"
                    >
                      {it.thickness_mm} mm
                    </span>
                  )}
                  <DepartmentBadges item={it} />
                </div>
                <div className="font-mono-num font-bold text-2xl mt-2 leading-none">
                  {fmtQty(it.current_qty)}
                  <span className="text-sm text-[#6B6B6B] ml-1">{it.unit}</span>
                </div>
                {packSummary(it) && (
                  <div
                    className="text-xs font-medium text-[#9A6F00] mt-1"
                    data-testid={`pack-summary-${it.id}`}
                  >
                    {packSummary(it)}
                  </div>
                )}
                <div className="text-xs text-[#6B6B6B] mt-1 truncate">
                  {it.supplier_default || "No default supplier"}
                </div>
                {it.order_contact_id && usersById[it.order_contact_id] && (
                  <div
                    className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold tracking-wider uppercase text-[#1F3B6B] bg-[#DFEEF7] border-2 border-[#001740] rounded px-1.5 py-0.5"
                    data-testid={`catalog-order-contact-${it.id}`}
                  >
                    <Users size={10} strokeWidth={2.5} />
                    Orders: {usersById[it.order_contact_id].name}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <button
                  data-testid={`edit-${it.id}`}
                  onClick={() => {
                    setEditing(it);
                    setOpen(true);
                  }}
                  className="nb-btn bg-white p-2"
                  aria-label="edit"
                >
                  <Edit3 size={16} strokeWidth={2.5} />
                </button>
                <button
                  data-testid={`history-${it.id}`}
                  onClick={() => setHistoryFor(it)}
                  className="nb-btn bg-white p-2"
                  aria-label="history"
                >
                  <HistoryIcon size={16} strokeWidth={2.5} />
                </button>
                {/* v182 — Add on-the-run to an open Draft PO (or start
                    one). Sticky-note replacement — noticed low stock on
                    the shelf? Add it now, Send when your bulk order day
                    arrives. */}
                <button
                  data-testid={`add-to-draft-${it.id}`}
                  onClick={() => setAddToDraftFor(it)}
                  className="nb-btn bg-[#DFEEF7] border-[#0A3A7A] text-[#0A3A7A] p-2"
                  aria-label="add to draft order"
                  title="Add this item to an open Draft PO (or start one)"
                >
                  <ShoppingCart size={16} strokeWidth={2.5} />
                </button>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full nb-card p-10 text-center">
            <div className="font-display text-2xl">No items yet</div>
            <div className="text-sm text-[#6B6B6B] mt-1">
              Click <b>Add item</b> to create your first catalog entry.
            </div>
          </div>
        )}
      </div>
      )}

      <StockFormDialog
        open={open}
        initial={editing}
        onClose={() => setOpen(false)}
        onSaved={handleSaved}
        onDelete={() => load()}
      />

      <BulkThresholdsDialog
        open={bulkThresholdsOpen}
        items={items}
        onClose={() => setBulkThresholdsOpen(false)}
        onSaved={load}
      />

      <AddToDraftDialog
        open={!!addToDraftFor}
        item={addToDraftFor}
        suppliers={suppliers}
        onClose={() => setAddToDraftFor(null)}
        onSaved={() => {
          setAddToDraftFor(null);
          window.dispatchEvent(new Event("stock-updated"));
        }}
      />

      <ItemHistoryDialog
        open={!!historyFor}
        item={historyFor}
        onClose={() => setHistoryFor(null)}
      />

      {lightboxFor && (
        <Lightbox paths={lightboxFor} onClose={() => setLightboxFor(null)} />
      )}

      <ManageStockTypesDialog
        open={manageTypesOpen}
        onClose={() => setManageTypesOpen(false)}
      />
      <ManageMaterialsDialog
        open={manageMatsOpen}
        onClose={() => setManageMatsOpen(false)}
      />
      <ManageColoursDialog
        open={manageColoursOpen}
        onClose={() => setManageColoursOpen(false)}
      />
      <ManageDepartmentsDialog
        open={manageDeptsOpen}
        onClose={() => setManageDeptsOpen(false)}
      />
      <ManageStockCategoriesDialog
        open={manageCatsOpen}
        onClose={() => {
          setManageCatsOpen(false);
          load();
        }}
        onChanged={() => load()}
      />
      <ManageRequestersDialog
        open={manageRequestersOpen}
        onClose={() => setManageRequestersOpen(false)}
      />
    </div>
  );
};

export default Catalog;
